import { Color, Spacing } from '@persona-id/design-tokens';
import { CaretDownIcon } from '@persona-id/design-tokens/icons';
import { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { TransitionDuration } from 'design-system/variables/transitions';
import useToggle from 'lib/hooks/useToggle';

interface AccordionProps {
  /**
   * Render method the button trigger for collapsing and expanding the element
   */
  button: (expanded: boolean) => ReactNode;
  children: ReactNode;
  defaultExpanded?: boolean;
  disabled?: boolean;
}

const Accordion = ({ button, children, defaultExpanded = true, disabled }: AccordionProps) => {
  const [expanded, { toggle: toggleExpanded }] = useToggle(defaultExpanded);

  return (
    <Container aria-expanded={expanded ? 'true' : 'false'}>
      <ButtonContainer
        role='button'
        id='accordion__button'
        onClick={disabled ? undefined : toggleExpanded}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        disabled={disabled}
      >
        {button(expanded)}
        <StyledCaretDownIcon
          expanded={expanded}
          hidden={children == null}
          color={disabled ? Color.TextDisabled : Color.TextPrimary}
        />
      </ButtonContainer>

      {expanded && <Contents>{children}</Contents>}
    </Container>
  );
};

const Container = styled.div`
  border-bottom: 1px solid ${Color.BorderDivider};
  width: 100%;
`;

const ButtonContainer = styled.div<{ disabled: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  user-select: none;

  padding-left: ${Spacing[2]};
  padding-right: ${Spacing[1]};

  ${({ disabled, theme }) => css`
    cursor: ${disabled ? 'inherit' : 'pointer'};
    &:hover {
      background-color: ${disabled ? 'unset' : theme.color.background.highlight};
    }
  `}
`;

const StyledCaretDownIcon = styled(({ expanded, hidden, ...props }) => (
  <CaretDownIcon {...props} />
))<{
  expanded: boolean;
  hidden: boolean;
}>`
  transition: transform ${TransitionDuration.Fast};
  transform: rotate(${({ expanded }) => (expanded ? '0' : '-90deg')});

  width: 20px;
  height: 20px;

  ${({ hidden }) =>
    hidden &&
    css`
      opacity: 0;
    `}
`;

export const Contents = styled.div`
  padding-left: ${Spacing[2]};
  padding-right: ${Spacing[2]};
`;

export default Accordion;
